import { cn } from '@/lib/utils';
import { ImageIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { normalizeFileUrl } from '@/lib/attachments/utils';
import { useFrappeFileUrl } from '@/lib/utils/frappeUrl';
import { getPastelColor, getInitial } from './createThumbnailColumn';
import { useState, useEffect, useMemo } from 'react';

interface GalleryGridLayoutProps<T> {
  items: T[];
  getRowId: (row: T) => string;
  getImagePath: (row: T) => string | null;
  getTitleText: (row: T) => string;
  getSubtitleText?: (row: T) => string | undefined;
  /** Optional badge text overlay (top-right). Return falsy to hide. */
  getBadgeText?: (row: T) => string | undefined;
  onRowClick?: (row: T) => void;
  isLoading?: boolean;
  hasImageFields?: boolean;
  sizePx?: number;
  className?: string;
  /** Function to get child rows (e.g., variants). If provided, gallery flattens hierarchy. */
  getSubRows?: (row: T) => T[] | undefined;
}

interface GalleryCardProps<T> {
  item: T;
  rowId: string;
  imagePath: string | null;
  title: string;
  subtitle?: string;
  badgeText?: string;
  onRowClick?: (row: T) => void;
  isVariant?: boolean;
}

function GalleryCard<T>({
  item,
  rowId,
  imagePath,
  title,
  subtitle,
  badgeText,
  onRowClick,
  isVariant = false,
}: GalleryCardProps<T>) {
  const [imageFailed, setImageFailed] = useState(false);
  const toAbsolute = useFrappeFileUrl();

  // Get fallback color and initial
  const color = getPastelColor(title || rowId);
  const initial = getInitial(title || rowId);

  // Normalize image URL (same logic as ThumbnailCell)
  const rawPathStr = typeof imagePath === 'string' ? imagePath.trim() : '';
  const imageUrl = rawPathStr
    ? rawPathStr.startsWith('http://') ||
      rawPathStr.startsWith('https://') ||
      rawPathStr.startsWith('data:')
      ? rawPathStr
      : (() => {
          const normalized = normalizeFileUrl(rawPathStr);
          return normalized ? toAbsolute(normalized) : null;
        })()
    : null;

  // Reset image failed state when URL changes
  useEffect(() => {
    if (imageUrl) {
      setImageFailed(false);
    }
  }, [imageUrl]);

  const showImage = !!imageUrl && !imageFailed;
  const clickable = typeof onRowClick === 'function';

  return (
    <div
      role="gridcell"
      className={cn(
        'group relative flex flex-col rounded border border-border/60 bg-card overflow-hidden',
        'hover:shadow-lg hover:border-border transition-all duration-200',
        clickable && 'cursor-pointer'
      )}
      onClick={() => onRowClick?.(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onRowClick?.(item);
        }
      }}
      tabIndex={clickable ? 0 : -1}
    >
      {/* Variant indicator badge */}
      {isVariant && (
        <div className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
          Variant
        </div>
      )}
      {/* Custom badge (e.g., personalization) */}
      {badgeText && (
        <div className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 text-xs font-medium">
          {badgeText}
        </div>
      )}
      {/* Image area */}
      <div className="aspect-square w-full overflow-hidden bg-muted/30 flex items-center justify-center">
        {showImage ? (
          <img
            src={imageUrl}
            alt={title || 'Gallery item'}
            className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
            loading="lazy"
            decoding="async"
            draggable={false}
            onError={() => setImageFailed(true)}
          />
        ) : (
          <span
            className={cn(
              'flex h-full w-full items-center justify-center text-4xl font-medium',
              color.bg,
              color.text
            )}
          >
            {initial}
          </span>
        )}
      </div>

      {/* Text area */}
      <div className="p-3 space-y-0.5">
        <p className="font-medium text-sm truncate" title={title}>
          {title}
        </p>
        {subtitle && (
          <p className="text-xs text-muted-foreground truncate" title={subtitle}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}

export function GalleryGridLayout<T>({
  items,
  getRowId,
  getImagePath,
  getTitleText,
  getSubtitleText,
  getBadgeText,
  onRowClick,
  isLoading = false,
  hasImageFields = true,
  sizePx: _sizePx = 160,
  className,
  getSubRows,
}: GalleryGridLayoutProps<T>) {
  // Flatten items to include children (variants) if getSubRows is provided
  const flattenedItems = useMemo(() => {
    if (!getSubRows) return items.map((item) => ({ item, isVariant: false }));

    const result: { item: T; isVariant: boolean }[] = [];
    for (const item of items) {
      result.push({ item, isVariant: false });
      const subRows = getSubRows(item);
      if (subRows && subRows.length > 0) {
        for (const subRow of subRows) {
          result.push({ item: subRow, isVariant: true });
        }
      }
    }
    return result;
  }, [items, getSubRows]);

  // Loading state - show skeletons
  if (isLoading) {
    return (
      <div
        className={cn(
          'grid gap-4',
          'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5',
          className
        )}
      >
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex flex-col rounded border border-border/60 overflow-hidden">
            <Skeleton className="aspect-square w-full" />
            <div className="p-3 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (flattenedItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <ImageIcon className="h-12 w-12 mb-4 opacity-50" />
        <p>No items to display</p>
      </div>
    );
  }

  // No image fields detected - show message but still render cards with fallback
  if (!hasImageFields) {
    // Still render, just with fallback avatars
  }

  return (
    <div
      role="grid"
      aria-label="Gallery view"
      className={cn(
        'grid gap-4',
        'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5',
        className
      )}
    >
      {flattenedItems.map(({ item, isVariant }) => {
        const rowId = getRowId(item);
        const title = getTitleText(item);
        const subtitle = getSubtitleText?.(item);
        const badgeText = getBadgeText?.(item);
        const imagePath = getImagePath(item);

        return (
          <GalleryCard<T>
            key={rowId}
            item={item}
            rowId={rowId}
            imagePath={imagePath}
            title={title}
            subtitle={subtitle}
            badgeText={badgeText}
            onRowClick={onRowClick}
            isVariant={isVariant}
          />
        );
      })}
    </div>
  );
}
